/**
 * Enveloppe minimale autour d'IndexedDB, sans dépendance externe.
 * Deux magasins : `cache` (dernière réponse connue par requête) et `outbox`
 * (mutations en attente d'envoi). Suffit au mode hors connexion de l'app.
 */

const DB_VERSION = 1;
const STORE_CACHE = 'cache';
const STORE_OUTBOX = 'outbox';

export interface CacheEntry<T = unknown> {
  key: string;
  value: T;
  storedAt: number;
}

export interface OutboxEntry {
  /** Clé auto-incrémentée, sert d'ordre d'envoi (FIFO). */
  id?: number;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body: unknown;
  /** Identifiant optimiste attribué côté client, réutilisé par le serveur. */
  clientId: string | null;
  label: string;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

function idbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export class LocalStore {
  private dbPromise: Promise<IDBDatabase> | null = null;
  /** Repli en mémoire quand IndexedDB est indisponible (Safari privé, SSR, tests). */
  private memoryCache = new Map<string, CacheEntry>();
  private memoryOutbox: OutboxEntry[] = [];
  private memorySeq = 1;

  private readonly dbName: string;

  constructor(dbName: string) {
    this.dbName = dbName;
  }

  private open(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(STORE_CACHE)) {
            db.createObjectStore(STORE_CACHE, { keyPath: 'key' });
          }
          if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
            db.createObjectStore(STORE_OUTBOX, { keyPath: 'id', autoIncrement: true });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    }
    return this.dbPromise;
  }

  private async tx(store: string, mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.open();
    return db.transaction(store, mode).objectStore(store);
  }

  async getCache<T>(key: string): Promise<CacheEntry<T> | null> {
    if (!idbAvailable()) return (this.memoryCache.get(key) as CacheEntry<T>) ?? null;
    try {
      const store = await this.tx(STORE_CACHE, 'readonly');
      const entry = await promisify<CacheEntry<T> | undefined>(store.get(key));
      return entry ?? null;
    } catch {
      return null;
    }
  }

  async setCache<T>(key: string, value: T): Promise<void> {
    const entry: CacheEntry<T> = { key, value, storedAt: Date.now() };
    if (!idbAvailable()) {
      this.memoryCache.set(key, entry);
      return;
    }
    try {
      const store = await this.tx(STORE_CACHE, 'readwrite');
      await promisify(store.put(entry));
    } catch {
      this.memoryCache.set(key, entry);
    }
  }

  /** Invalide les entrées dont la clé commence par l'un des préfixes. */
  async invalidate(prefixes: string[]): Promise<void> {
    if (prefixes.length === 0) return;
    const matches = (key: string) => prefixes.some((p) => key.startsWith(p));
    if (!idbAvailable()) {
      for (const key of [...this.memoryCache.keys()]) {
        if (matches(key)) this.memoryCache.delete(key);
      }
      return;
    }
    try {
      const store = await this.tx(STORE_CACHE, 'readwrite');
      const keys = await promisify<IDBValidKey[]>(store.getAllKeys());
      for (const key of keys) {
        if (typeof key === 'string' && matches(key)) store.delete(key);
      }
    } catch {
      /* le cache est un accélérateur : un échec d'invalidation n'est pas bloquant */
    }
  }

  async clearAll(): Promise<void> {
    this.memoryCache.clear();
    this.memoryOutbox = [];
    if (!idbAvailable()) return;
    try {
      const cache = await this.tx(STORE_CACHE, 'readwrite');
      await promisify(cache.clear());
      const outbox = await this.tx(STORE_OUTBOX, 'readwrite');
      await promisify(outbox.clear());
    } catch {
      /* ignoré */
    }
  }

  // -- File d'attente des mutations ----------------------------------------

  async enqueue(entry: Omit<OutboxEntry, 'id' | 'createdAt' | 'attempts' | 'lastError'>): Promise<number> {
    const record: OutboxEntry = { ...entry, createdAt: Date.now(), attempts: 0, lastError: null };
    if (!idbAvailable()) {
      const id = this.memorySeq++;
      this.memoryOutbox.push({ ...record, id });
      return id;
    }
    try {
      const store = await this.tx(STORE_OUTBOX, 'readwrite');
      const key = await promisify<IDBValidKey>(store.add(record));
      return Number(key);
    } catch {
      const id = this.memorySeq++;
      this.memoryOutbox.push({ ...record, id });
      return id;
    }
  }

  async listOutbox(): Promise<OutboxEntry[]> {
    if (!idbAvailable()) return [...this.memoryOutbox];
    try {
      const store = await this.tx(STORE_OUTBOX, 'readonly');
      const all = await promisify<OutboxEntry[]>(store.getAll());
      return all.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    } catch {
      return [...this.memoryOutbox];
    }
  }

  async updateOutbox(entry: OutboxEntry): Promise<void> {
    if (!idbAvailable()) {
      const index = this.memoryOutbox.findIndex((e) => e.id === entry.id);
      if (index >= 0) this.memoryOutbox[index] = entry;
      return;
    }
    try {
      const store = await this.tx(STORE_OUTBOX, 'readwrite');
      await promisify(store.put(entry));
    } catch {
      /* ignoré */
    }
  }

  async dequeue(id: number): Promise<void> {
    if (!idbAvailable()) {
      this.memoryOutbox = this.memoryOutbox.filter((e) => e.id !== id);
      return;
    }
    try {
      const store = await this.tx(STORE_OUTBOX, 'readwrite');
      await promisify(store.delete(id));
    } catch {
      this.memoryOutbox = this.memoryOutbox.filter((e) => e.id !== id);
    }
  }
}
